import { ReplaySubject, Subject, takeUntil } from 'rxjs';
/**
 * This class provides a base structure for other classes
 */
export class BaseClass {
    constructor(id = '', dependencies = {}) {
        /**
         * An RxJS subject that is used to signal destruction of the instance.
         * When subscribing to an observable provided by a class, you should takeUntil(instance.destroy$)
         */
        this.destroy$ = new Subject();
        this._propertySubjects = {};
        this.dependencies = dependencies;
        this.id = id;
        this.init();
    }
    /**
     * Just a utility function to allow us to override in a component and not have to define a constructor all the time
     */
    init() {
    }
    /**
     * Gets the value of the specified property from the instance.
     *
     * @param propertyName - The name of the property to retrieve.
     * @returns The value of the specified property.
     */
    propertyGet(propertyName) {
        return this[propertyName];
    }
    /**
     * Sets the value of the specified property on the instance.
     *
     * @param propertyName - The name of the property to set.
     * @param value - The value to set on the property.
     */
    propertySet(propertyName, value) {
        this[propertyName] = value;
        return this;
    }
    /**
     * This is for "set"/"get" properties which need to maintain a private _propertyName version of the property to store the current value.
     * @param propertyName
     * @param value
     * @protected
     */
    _propertySet(propertyName, value) {
        // Set the actual property value on the class instance
        this[`_${propertyName}`] = value;
        // Check if there's an existing ReplaySubject for this property
        if (this._propertySubjects[propertyName]) {
            // Emit the new value
            this._propertySubjects[propertyName].next(value);
        }
        return this;
    }
    /**
     * Returns an observable that will emit the value of the specified property.
     * If the property has been set to some value, the current value will be emitted immediately upon subscription.
     * Otherwise, emits when the property is set using `propertySet`.
     *
     * @param propertyName - The name of the property to observe.
     * @returns An observable that will emit the value of the specified property.
     */
    propertyGet$(propertyName) {
        // Create a ReplaySubject for the property if it doesn't exist
        if (!this._propertySubjects[propertyName]) {
            this._propertySubjects[propertyName] = new ReplaySubject(1);
            // only trigger the observable if this property has been set to some value. 
            if (typeof this[propertyName] !== 'undefined') {
                this._propertySubjects[propertyName].next(this[propertyName]);
            }
        }
        const observable = this._propertySubjects[propertyName].asObservable();
        return observable.pipe(takeUntil(this.destroy$));
    }
    propertiesLoad(properties) {
        for (const propertyName in properties) {
            this.propertySet(propertyName, properties[propertyName]);
        }
    }
    /**
     * Call this method to destroy this instance.
     * This will also complete the `destroy$` observable.
     *
     * @example
     * ```
     * const instance = new BaseClass();
     * instance.destroy();
     * ```
     */
    destroy() {
        this.dependencies = {};
        // Complete all property subjects
        for (const prop in this._propertySubjects) {
            this._propertySubjects[prop].complete();
        }
        this._propertySubjects = {};
        this.destroyed = true;
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
/**
 * SVN Version number of this class, auto-injected on commit.
 */
BaseClass.VERSION = '$Revision: 126916 $'; // TODO: make these populate with SVN
